import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react'

interface UseModalA11yOptions {
  onClose: () => void
  /** Si false, ESC no cierra (p.ej. mutación en curso). Default true. */
  closeEnabled?: boolean
  /**
   * Engancha/desengancha la conducta. Default true (montado = abierto).
   * Para modales con animación de salida (siguen montados mientras animan),
   * pasar `active={open}`.
   */
  active?: boolean
}

/**
 * Pila de modales abiertos. Con dos apilados (p.ej. "¿Descartar cambios?" encima de un
 * formulario en modal) solo el de ARRIBA atiende Esc y Tab: si no, Esc cerraba los dos y
 * el focus-trap del de abajo le robaba el foco al de arriba.
 */
const openPanels: HTMLElement[] = []

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Centraliza la conducta de accesibilidad de un modal sobre el panel apuntado
 * por `panelRef`: scroll-lock del body, cierre con ESC, focus-trap (Tab /
 * Shift+Tab dentro del panel), foco inicial al primer focusable y restauración
 * del foco al elemento que abrió el modal.
 *
 * No renderiza nada — lo usa `<Modal>` y los casos que no pueden pasar por el
 * wrapper (p.ej. `SessionPreviewModal`, con framer-motion).
 */
export function useModalA11y(
  panelRef: RefObject<HTMLElement>,
  { onClose, closeEnabled = true, active = true }: UseModalA11yOptions,
): void {
  // Refs para leer valores frescos dentro del listener sin re-suscribirlo.
  // Se actualizan tras el commit (no en render: React puede descartar un render y
  // la mutación se filtraría). Layout effect → ya frescos antes de cualquier evento.
  const onCloseRef = useRef(onClose)
  const closeEnabledRef = useRef(closeEnabled)
  useLayoutEffect(() => {
    onCloseRef.current = onClose
    closeEnabledRef.current = closeEnabled
  })

  useEffect(() => {
    if (!active) return
    const node = panelRef.current
    if (!node) return
    // Const con tipo no-nullable para conservar el narrowing dentro de los
    // closures (onKeyDown / focusables).
    const panel: HTMLElement = node

    const previouslyFocused = document.activeElement as HTMLElement | null

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusables = () => Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))

    // Foco inicial: primer focusable del panel; si no hay, el panel (tabIndex=-1).
    const first = focusables()[0]
    ;(first ?? panel).focus()

    openPanels.push(panel)

    function onKeyDown(e: KeyboardEvent) {
      if (openPanels[openPanels.length - 1] !== panel) return
      if (e.key === 'Escape') {
        if (closeEnabledRef.current) onCloseRef.current()
        return
      }
      if (e.key !== 'Tab') return

      const items = focusables()
      const firstEl = items[0]
      const lastEl = items[items.length - 1]
      if (!firstEl || !lastEl) {
        e.preventDefault()
        panel.focus()
        return
      }
      const activeEl = document.activeElement
      if (e.shiftKey) {
        if (activeEl === firstEl || !panel.contains(activeEl)) {
          e.preventDefault()
          lastEl.focus()
        }
      } else if (activeEl === lastEl || !panel.contains(activeEl)) {
        e.preventDefault()
        firstEl.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      openPanels.splice(openPanels.lastIndexOf(panel), 1)
      document.body.style.overflow = prevOverflow
      previouslyFocused?.focus?.()
    }
  }, [active, panelRef])
}
